import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "src/components/card";
import { itaQueryOptions, itaMutations } from "src/api/mvte/ita-query-options";
import { SteelUser } from "../types/SteelUserResponse";
import { CreateSteelUserPayload } from "../types/CreateSteelUserPayload";
import { UpdateSteelUserPayload } from "../types/UpdateSteelUserPayload";
import { FieldError } from "./FieldError";
import { RequiredMark } from "./RequiredMark";

type PanelMode = "closed" | "edit" | "create";
type UserForm = Pick<SteelUser, "userName" | "userLogin" | "userEmail" | "activeTF"> & { defaultLocationID: number | null };

type FieldKey =
  | "userName"
  | "userLogin"
  | "userEmail"
  | "password"
  | "confirmPassword";

const FIELD_LABELS: Record<FieldKey, string> = {
  userName: "Name",
  userLogin: "Login",
  userEmail: "Email",
  password: "Password",
  confirmPassword: "Confirm Password",
};

const EMPTY_FORM: UserForm = {
  userName: "",
  userLogin: "",
  userEmail: "",
  defaultLocationID: null,
  activeTF: true,
};

const MIN_PASSWORD_LENGTH = 8;

const normalizeLocationID = (id: number | null | undefined): number | null => id ? id : null;

const toForm = (user: SteelUser): UserForm => ({
  userName: user.userName,
  userLogin: user.userLogin,
  userEmail: user.userEmail,
  defaultLocationID: normalizeLocationID(user.defaultLocationID),
  activeTF: user.activeTF,
});

const baseInputClass = "w-full rounded-md border px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1";

const inputClassFor = (hasError: boolean) =>`${baseInputClass} ${
  hasError ? "border-red-500 focus:border-red-500 focus:ring-red-500": "border-gray-300 focus:border-[#0B1D3A] focus:ring-[#0B1D3A]"
}`;

const labelClass = "mb-1 block text-sm font-medium text-gray-900";

  function SteelApp() {
  const [users, setUsers] = useState<SteelUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<SteelUser | null>(null);
  const [mode, setMode] = useState<PanelMode>("closed");
  const [searchTerm, setSearchTerm] = useState("");

  const [draft, setDraft] = useState<UserForm | null>(null);
  const [draftRoleIDs, setDraftRoleIDs] = useState<number[]>([]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const rolesQuery = useQuery({
    ...itaQueryOptions.itaSteelRolesQueryOptions(),
  });
  const steelUsers = useQuery({
    ...itaQueryOptions.itaSteelUserQueryOptions(),
  });
  const locationsQuery = useQuery({
    ...itaQueryOptions.itaSteelLocationsQueryOptions(),
  });

  const itaCreateMutation = itaMutations.useCreateSteelUser();
  const itaUpdateMutation = itaMutations.useUpdateSteelUserRoles();
  const itaUpdateStatusMutation = itaMutations.useUpdateSteelUserStatus();

  useEffect(() => {
    if (steelUsers.data) {
      setUsers(steelUsers.data);
    }
  }, [steelUsers.data]);

  const userRolesQuery = useQuery({
    ...itaQueryOptions.itaSteelUserRolesQueryOptions({
      params: { userID: selectedUser?.userID ?? 0 },
    }),
    enabled: mode === "edit" && selectedUser !== null,
  });

  useEffect(() => {
    if (mode !== "edit") return;
    if (userRolesQuery.data) {
      setDraftRoleIDs(userRolesQuery.data);
    }
  }, [userRolesQuery.data, mode, selectedUser?.userID]);

  const allRoles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data]);
  type SteelRole = (typeof allRoles)[number];

  const locations = useMemo(
  () =>
    (locationsQuery.data ?? [])
      .filter((l) => l.locationID != null && l.locationName)
      .map((l) => ({ id: l.locationID, name: l.locationName }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  [locationsQuery.data],
  );

  const rolesLoading =
    rolesQuery.isPending || (mode === "edit" && userRolesQuery.isPending);

  const savedRoleIDs = useMemo(
    () => userRolesQuery.data ?? [],
    [userRolesQuery.data],
  );

  const roleGroups = useMemo(() => {
    const groups = new Map<string, SteelRole[]>();
    for (const role of allRoles) {
      const app = role.applicationName?.trim() || "Other";
      const bucket = groups.get(app);
      if (bucket) {
        bucket.push(role);
      } else {
        groups.set(app, [role]);
      }
    }
    return [...groups.entries()]
      .map(([applicationName, roles]) => ({
        applicationName,
        roles: [...roles].sort((a, b) => a.roleName.localeCompare(b.roleName)),
      }))
      .sort((a, b) => a.applicationName.localeCompare(b.applicationName));
  }, [allRoles]);

  const statusChanged =
    !!draft && !!selectedUser && draft.activeTF !== selectedUser.activeTF;

  const detailsChanged =
    !!draft &&
    !!selectedUser &&
    (draft.userName !== selectedUser.userName ||
      draft.userLogin !== selectedUser.userLogin ||
      draft.userEmail !== selectedUser.userEmail ||
      draft.defaultLocationID !==
        normalizeLocationID(selectedUser.defaultLocationID));

  const rolesChanged =
    draftRoleIDs.length !== savedRoleIDs.length ||
    draftRoleIDs.some((id) => !savedRoleIDs.includes(id));

  const hasChanges = statusChanged || detailsChanged || rolesChanged;

  const errors = useMemo(() => {
    const result: Partial<Record<FieldKey, string>> = {};
    if (!draft) return result;

    if (!draft.userName.trim()) result.userName = "Name is required.";
    if (!draft.userLogin.trim()) result.userLogin = "Login is required.";
    if (!draft.userEmail.trim()) result.userEmail = "Email is required.";

    if (mode === "create") {
      if (!password) {
        result.password = "Password is required.";
      } else if (password.length < MIN_PASSWORD_LENGTH) {
        result.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
      }

      if (!confirmPassword) {
        result.confirmPassword = "Please confirm the password.";
      } else if (password !== confirmPassword) {
        result.confirmPassword = "Passwords don't match.";
      }
    }
    return result;
  }, [draft, mode, password, confirmPassword]);

  const errorKeys = Object.keys(errors) as FieldKey[];

  const errorFor = (field: FieldKey): string | undefined => {
    if (showErrors) return errors[field];
    if (field === "password" && password.length > 0) {
      return password.length < MIN_PASSWORD_LENGTH ? errors.password : undefined;
    }
    if (field === "confirmPassword" && confirmPassword.length > 0) {
      return password !== confirmPassword ? errors.confirmPassword : undefined;
    }
    return undefined;
  };

  const canSave = mode === "create" ? true : hasChanges;

  const filteredUsers = users.filter((user) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return [user.userName, user.userLogin, user.userEmail].some((field) =>
      field?.toLowerCase().includes(term),
    );
  });

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const resetPanel = () => {
    setMode("closed");
    setSelectedUser(null);
    setDraft(null);
    setDraftRoleIDs([]);
    setPassword("");
    setConfirmPassword("");
    setSaveError(null);
    setShowErrors(false);
  };

  const handleSelectUser = (user: SteelUser) => {
    if (mode === "edit" && selectedUser?.userID === user.userID) return;
    setMode("edit");
    setSelectedUser(user);
    setDraft(toForm(user));
    setDraftRoleIDs([]);
    setPassword("");
    setConfirmPassword("");
    setSaveError(null);
    setShowErrors(false);
  };

  const handleCreateUser = () => {
    setMode("create");
    setSelectedUser(null);
    setDraft({ ...EMPTY_FORM });
    setDraftRoleIDs([]);
    setPassword("");
    setConfirmPassword("");
    setSaveError(null);
    setShowErrors(false);
  };

  const handleDraftChange = <K extends keyof UserForm>(
    field: K,
    value: UserForm[K],
  ) => {
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleToggleRole = (roleID: number) => {
    setDraftRoleIDs((prev) =>
      prev.includes(roleID)
        ? prev.filter((id) => id !== roleID)
        : [...prev, roleID],
    );
  };

  const handleCancel = () => {
    resetPanel();
  };

  const handleSave = async () => {
    if (!draft || isSaving) return;
    if (mode === "edit" && !hasChanges) return;
    if (errorKeys.length > 0) {
      setShowErrors(true);
      setSaveError(null);
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      if (mode === "create") {
        const payload: CreateSteelUserPayload = {
          userLogin: draft.userLogin.trim(),
          userName: draft.userName.trim(),
          userEmail: draft.userEmail.trim(),
          defaultLocationID: draft.defaultLocationID,
          activeTF: draft.activeTF,
          password,
          roleIDs: draftRoleIDs,
        };

        await itaCreateMutation.mutateAsync({ payload });
        await steelUsers.refetch();
        resetPanel();
        return;
      }

      if (!selectedUser) return;

      if (statusChanged) {
        await itaUpdateStatusMutation.mutateAsync({
          isActive: draft.activeTF,
          userID: selectedUser.userID,
        });
      }

      const payload: UpdateSteelUserPayload = {
        userLogin: draft.userLogin.trim(),
        userName: draft.userName.trim(),
        defaultLocationID: draft.defaultLocationID,
        activeTF: draft.activeTF,
        userEmail: draft.userEmail.trim(),
        roleIDs: draftRoleIDs,
      };

      await itaUpdateMutation.mutateAsync({
        userID: selectedUser.userID,
        payload,
      });

      await Promise.all([steelUsers.refetch(), userRolesQuery.refetch()]);

      setSelectedUser({
        ...selectedUser,
        ...draft,
        defaultLocationID: draft.defaultLocationID ?? 0,
      });
    } catch (error) {
      console.error("Failed to save Steel user changes", error);
      setSaveError(
        mode === "create"
          ? "Couldn't create the user. Check the login and email aren't already in use, then try again."
          : "Couldn't save the changes. Try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const panelOpen = mode !== "closed";
  const listVisible = mode !== "create";

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      {listVisible && (
        <div
          className={`w-full transition-all duration-300 ease-in-out ${
            panelOpen ? "lg:w-1/2" : "lg:w-full"
          }`}>
          <Card>
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Users ({users.length})
                </h2>
                <button
                  type="button"
                  onClick={handleCreateUser}
                  className="cursor-pointer rounded-md bg-[#0078d4] px-4 py-2 text-sm font-medium text-white hover:bg-[#0078d4]/90">
                  + Create User
                </button>
              </div>

              <input
                type="text"
                placeholder="Search by name, login, or email..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-[#0B1D3A] focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]"
              />

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">
                        Name
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">
                        Login
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">
                        Email
                      </th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-10 text-center text-sm text-gray-400">
                          No users to display.
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => (
                        <tr
                          key={user.userID}
                          onClick={() => handleSelectUser(user)}
                          className={`cursor-pointer hover:bg-gray-50 ${
                            selectedUser?.userID === user.userID
                              ? "bg-blue-50"
                              : ""
                          }`}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {user.userName}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {user.userLogin}
                          </td>
                          <td className="w-1/2 px-4 py-2 text-sm text-gray-900">
                            {user.userEmail}
                          </td>
                          <td className="px-4 py-2 text-right text-sm text-[#0078d4]">
                            {">"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </div>
      )}

      {panelOpen && draft && (
        <div
          className={`flex w-full flex-col gap-4 transition-all duration-300 ease-in-out ${
            mode === "create" ? "lg:w-full" : "lg:w-1/2"
          }`}>
          <Card>
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {mode === "create" ? "New User" : "User Details"}
                </h2>
                <p className="text-xs text-gray-500">
                  <span className="text-red-600">*</span> Required
                </p>
              </div>

              <div
                className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${
                  mode === "create" ? "lg:grid-cols-3" : ""
                }`}>
                <div>
                  <label className={labelClass} htmlFor="steel-user-name">
                    Name
                    <RequiredMark />
                  </label>
                  <input
                    id="steel-user-name"
                    type="text"
                    value={draft.userName}
                    aria-required="true"
                    aria-invalid={!!errorFor("userName")}
                    onChange={(e) =>
                      handleDraftChange("userName", e.target.value)
                    }
                    className={inputClassFor(!!errorFor("userName"))}
                  />
                  <FieldError message={errorFor("userName")} />
                </div>

                <div>
                  <label className={labelClass} htmlFor="steel-user-login">
                    Login
                    <RequiredMark />
                  </label>
                  <input
                    id="steel-user-login"
                    type="text"
                    value={draft.userLogin}
                    aria-required="true"
                    aria-invalid={!!errorFor("userLogin")}
                    onChange={(e) =>
                      handleDraftChange("userLogin", e.target.value)
                    }
                    className={inputClassFor(!!errorFor("userLogin"))}
                  />
                  <FieldError message={errorFor("userLogin")} />
                </div>

                <div>
                  <label className={labelClass} htmlFor="steel-user-email">
                    Email
                    <RequiredMark />
                  </label>
                  <input
                    id="steel-user-email"
                    type="email"
                    value={draft.userEmail}
                    aria-required="true"
                    aria-invalid={!!errorFor("userEmail")}
                    onChange={(e) =>
                      handleDraftChange("userEmail", e.target.value)
                    }
                    className={inputClassFor(!!errorFor("userEmail"))}
                  />
                  <FieldError message={errorFor("userEmail")} />
                </div>

                <div>
                  <label className={labelClass} htmlFor="steel-user-location">
                    Default Location{" "}
                    <span className="text-xs font-normal text-gray-500">
                      (optional)
                    </span>
                  </label>
                  <select
                    id="steel-user-location"
                    value={
                      draft.defaultLocationID === null
                        ? ""
                        : String(draft.defaultLocationID)
                    }
                    disabled={locationsQuery.isPending}
                    onChange={(e) =>
                      handleDraftChange(
                        "defaultLocationID",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    className={`${inputClassFor(false)} cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`}>
                    <option value="">
                      {locationsQuery.isPending
                        ? "Loading locations..."
                        : "No default location"}
                    </option>
                    {locations.map((location) => (
                      <option key={location.id} value={String(location.id)}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                  {locationsQuery.isError && (
                    <p className="mt-1 text-xs text-red-600">
                      Couldn't load locations.
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass} htmlFor="steel-user-status">
                    Status
                    <RequiredMark />
                  </label>
                  <select
                    id="steel-user-status"
                    value={draft.activeTF ? "Active" : "Disabled"}
                    onChange={(e) =>
                      handleDraftChange("activeTF", e.target.value === "Active")
                    }
                    className={`w-full cursor-pointer rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B1D3A] ${
                      draft.activeTF
                        ? "border-green-300 bg-green-50 text-green-800"
                        : "border-gray-300 bg-gray-50 text-gray-700"
                    }`}>
                    <option value="Active">Active</option>
                    <option value="Disabled">Disabled</option>
                  </select>
                </div>

                {mode === "create" && (
                  <>
                    <div>
                      <label
                        className={labelClass}
                        htmlFor="steel-user-password">
                        Password
                        <RequiredMark />
                      </label>
                      <input
                        id="steel-user-password"
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        aria-required="true"
                        aria-invalid={!!errorFor("password")}
                        onChange={(e) => setPassword(e.target.value)}
                        className={inputClassFor(!!errorFor("password"))}
                      />
                      <FieldError message={errorFor("password")} />
                    </div>

                    <div>
                      <label className={labelClass} htmlFor="steel-user-confirm">
                        Confirm Password
                        <RequiredMark />
                      </label>
                      <input
                        id="steel-user-confirm"
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        aria-required="true"
                        aria-invalid={!!errorFor("confirmPassword")}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={inputClassFor(!!errorFor("confirmPassword"))}
                      />
                      <FieldError message={errorFor("confirmPassword")} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <div>
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Application Roles
                  </h2>
                  <p className="text-sm text-gray-500">
                    Select the roles this user should have, grouped by
                    application. Hover the info icon to see what a role grants.
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4">
                {rolesLoading ? (
                  <p className="text-sm text-gray-400">Loading roles...</p>
                ) : roleGroups.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    No roles available to assign.
                  </p>
                ) : (
                  <div className="flex flex-col gap-5">
                    {roleGroups.map((group) => (
                      <fieldset
                        key={group.applicationName}
                        className="border-0 p-0">
                        <legend className="mb-2 w-full border-b border-gray-200 pb-1 text-sm font-semibold text-gray-900">
                          {group.applicationName}
                        </legend>

                        <div
                          className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${
                            mode === "create" ? "lg:grid-cols-3" : ""
                          }`}>
                          {group.roles.map((role) => (
                            <div
                              key={role.roleID}
                              className="flex items-center gap-1.5">
                              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-900">
                                <input
                                  type="checkbox"
                                  checked={draftRoleIDs.includes(role.roleID)}
                                  onChange={() => handleToggleRole(role.roleID)}
                                  className="h-4 w-4 cursor-pointer rounded border-gray-300 text-[#0078d4] focus:ring-[#0078d4]"
                                />
                                {role.roleName}
                              </label>

                              {role.roleDescription && (
                                <span className="group relative inline-flex">
                                  <span
                                    tabIndex={0}
                                    role="img"
                                    aria-label={`${group.applicationName} ${role.roleName}: ${role.roleDescription}`}
                                    className="flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-gray-300 text-[10px] font-semibold leading-none text-gray-500 focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]">
                                    i
                                  </span>
                                  <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-56 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
                                    {role.roleDescription}
                                  </span>
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </fieldset>
                    ))}
                  </div>
                )}
              </div>

              {showErrors && errorKeys.length > 0 && (
                <div
                  role="alert"
                  className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                  Please fix the following before saving:{" "}
                  {errorKeys.map((key) => FIELD_LABELS[key]).join(", ")}.
                </div>
              )}

              {saveError && (
                <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                  {saveError}
                </p>
              )}

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="cursor-pointer rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSave || isSaving}
                  className="cursor-pointer rounded-md bg-[#0078d4] px-4 py-2 text-sm font-medium text-white hover:bg-[#0078d4]/90 disabled:cursor-not-allowed disabled:opacity-50">
                  {isSaving
                    ? mode === "create"
                      ? "Creating..."
                      : "Saving..."
                    : mode === "create"
                      ? "Create User"
                      : "Save Changes"}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export { SteelApp };